import Cookies from 'js-cookie';

function getGAClientId() {
  const cookieValue = Cookies.get('_ga');

  if (!cookieValue) return '';

  const match = cookieValue.split('.');

  if (!match[2] || !match[3]) return '';

  return `${match[2]}.${match[3]}`;
}

const gaKeysMapping = {
  s: 'sessionId',
  o: 'sessionNumber',
  g: 'sessionEngaged',
  t: 'lastHitTimestamp',
  j: 'joinTimer',
  l: 'enhancedUserIdLoggedInState',
  h: 'enhancedUserId',
  d: 'joinId',
} as const;

type GaCookieKeys = keyof typeof gaKeysMapping;
type GaCookieValues = (typeof gaKeysMapping)[GaCookieKeys];
type GaCookieData = Record<GaCookieValues, string>;

const parseGoogleStreamCookie = function parseGoogleStreamCookie(
  str: string,
): GaCookieData {
  const [version, , data] = str.split('.');

  if (version === 'GS1') {
    const values = str.split('.').slice(2);

    const keys: GaCookieKeys[] = ['s', 'o', 'g', 't', 'j', 'l', 'h', 'd'];

    return Object.fromEntries(
      keys.map((k, i) => [k, values[i] || '']),
    ) as GaCookieData;
  }

  return Object.fromEntries(
    data!
      .replace(/%24/g, '$')
      .split('$')
      .map((s: string) => [
        gaKeysMapping[s[0] as GaCookieKeys] || s[0],
        decodeURIComponent(s.slice(1)),
      ]),
  ) as GaCookieData;
};

function getGASession() {
  const cookieValue = Cookies.get('_ga_E1HB7EM8VF');

  if (!cookieValue) {
    return {
      id: '',
      number: '',
    };
  }

  try {
    const cookieData = parseGoogleStreamCookie(cookieValue);

    return {
      id: cookieData.sessionId || '',
      number: cookieData.sessionNumber || '',
    };
  } catch (_error) {
    return {
      id: '',
      number: '',
    };
  }
}

export function getGASessionParams() {
  const clientId = getGAClientId();
  const session = getGASession();

  return {
    ga_client_id: clientId,
    ga_session_id: session.id,
    ga_session_number: session.number,
  };
}

type GtagOptions = {
  event_category?: string;
  event_label?: string;
  send_to?: string;
};

export function sendGtagEvent(eventName: string, options: GtagOptions) {
  if (window.gtag !== undefined) {
    window.gtag('event', eventName, options);
  }
}

type GtagDatalayerOptions = {
  event: string;
  gateway: string;
  product?: string;
  error_message?: string;
};

export function sendGtagDatalayerEvent(options: GtagDatalayerOptions) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(options);
}

export function getActiveTestParams(): Record<string, string> {
  const KEY_START_PATTERN = 'abtest_';
  const allCookies = Cookies.get();

  const activeTestsMap = Object.entries(allCookies).reduce(
    (acc, [key, value]) => {
      if (key.startsWith(KEY_START_PATTERN)) {
        acc[`exp_${key}`] = value;
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  return activeTestsMap;
}

export function getActiveExtensionTestParams(): Record<string, string> {
  type AbTestCookieConfig = {
    group: number;
    slug: string;
  };

  const AB_TEST_COOKIES_NAME = 'veepn-ab-tests';

  const cookieValue = Cookies.get(AB_TEST_COOKIES_NAME);

  if (!cookieValue) return {};

  try {
    const testConfigs = JSON.parse(cookieValue) as AbTestCookieConfig[];

    if (Array.isArray(testConfigs)) {
      const testMap = testConfigs.reduce(
        (acc, testConfig) => ({
          ...acc,
          [`exp_${testConfig.slug}`]: testConfig.group.toString(),
        }),
        {} as Record<string, string>,
      );

      return testMap;
    }
    return {};
  } catch (_error) {
    return {};
  }
}

export function getUtmMarkers() {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  const source = params.get('utm_source') ?? '';
  const medium = params.get('utm_medium') ?? '';
  const campaign = params.get('utm_campaign') ?? '';

  return {
    source,
    medium,
    campaign,
  };
}
